// web/src/components/ceremony/QuorumRoster.tsx — the live quorum roster beside the
// safe (wireframe 06). One row per eligible holder with a per-holder state icon:
// submitted · working · pending · declined. Pure presentational; the container
// owns the state. Token-only.
import React from 'react';
import { CheckCircle2, Loader, Circle, XCircle } from 'lucide-react';
import { Icon } from '../ui/Icon';

export type HolderState = 'submitted' | 'working' | 'pending' | 'declined';

export interface RosterHolder {
  memberId: string;
  label: string; // display name / email
  meta?: string; // fingerprint or auth method
  state: HolderState;
}

const ICONS: Record<HolderState, typeof CheckCircle2> = {
  submitted: CheckCircle2,
  working: Loader,
  pending: Circle,
  declined: XCircle,
};
const STATE_CLASS: Record<HolderState, string> = {
  submitted: 'bgStateSubmitted',
  working: 'bgStateWorking',
  pending: 'bgStatePending',
  declined: 'bgStateDeclined',
};
const STATE_LABEL: Record<HolderState, string> = {
  submitted: 'shard submitted',
  working: 'decrypting / sending…',
  pending: 'notified',
  declined: 'declined',
};

export function QuorumRoster({
  holders,
  thresholdK,
  totalN,
}: {
  holders: RosterHolder[];
  thresholdK: number;
  totalN: number;
}): React.JSX.Element {
  return (
    <div className="bgQuorumPanel">
      <header
        style={{
          display: 'flex',
          alignItems: 'baseline',
          justifyContent: 'space-between',
          gap: '1rem',
          padding: '0.9rem 1rem',
          borderBottom: '1px solid var(--color-border)',
        }}
      >
        <span className="kicker">Quorum</span>
        <span
          style={{
            fontFamily: 'var(--font-mono)',
            fontSize: 'var(--size-field-label)',
            color: 'var(--color-text2)',
          }}
        >
          requires {thresholdK} of {totalN}
        </span>
      </header>
      <div className="bgQuorumBody">
        {holders.length === 0 ? (
          <div className="bgRosterRow">
            <span className="bgRosterMeta">
              Waiting for the quorum — no holders have joined yet.
            </span>
          </div>
        ) : (
          holders.map((h) => (
            <div className="bgRosterRow" key={h.memberId}>
              <span className={`bgRosterIcon ${STATE_CLASS[h.state]}`} aria-hidden="true">
                <Icon icon={ICONS[h.state]} size="sm" />
              </span>
              <span className="bgRosterMain">
                <span className="bgRosterName">{h.label}</span>
                {h.meta && <span className="bgRosterMeta">{h.meta}</span>}
              </span>
              <span className="bgRosterStatus">{STATE_LABEL[h.state]}</span>
            </div>
          ))
        )}
      </div>
    </div>
  );
}

export default QuorumRoster;
